import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Modal from '../common/Modal';
import LoadingSpinner from '../common/LoadingSpinner';
import { apiClient } from '../../services/apiClient';

const ContextDocumentSelector = ({ isOpen, onClose, onConfirm }) => {
  const [contextDocs, setContextDocs] = useState([]);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadContextDocuments();
    }
  }, [isOpen]);

  const loadContextDocuments = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.getActiveContextDocuments();

      if (response.success) {
        setContextDocs(response.data.documents || []);
        // Pre-select all active documents
        setSelectedDocIds(response.data.documents.map(doc => doc.document_id));
      }
    } catch (err) {
      setError('Failed to load context documents');
      console.error('Error loading context documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleDoc = (docId) => {
    setSelectedDocIds(prev =>
      prev.includes(docId)
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const selectAll = () => {
    setSelectedDocIds(contextDocs.map(doc => doc.document_id));
  };

  const clearAll = () => {
    setSelectedDocIds([]);
  };

  const handleConfirm = () => {
    if (selectedDocIds.length === 0) {
      setError('Please select at least one context document');
      return;
    }
    onConfirm(selectedDocIds);
  };

  const getDocumentTypeIcon = (type) => {
    const icons = {
      'capabilities': 'list-check',
      'past_performance': 'trophy',
      'company_overview': 'building',
      'technical': 'cogs',
      'pricing': 'dollar-sign',
      'certifications': 'certificate'
    };
    return icons[type] || 'file-alt';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Select Company Context Documents"
      widthClassName="max-w-4xl"
    >
      <div className="space-y-4">
        {/* Header Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <FontAwesomeIcon icon="info-circle" className="text-blue-500 mt-1" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">
                Select company documents to compare against RFP requirements
              </p>
              <p className="text-blue-700">
                The AI will analyze how well your company's capabilities match the RFP requirements based on these documents.
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="py-12 text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-slate-600">Loading context documents...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <FontAwesomeIcon icon="exclamation-triangle" className="mr-2" />
            {error}
          </div>
        ) : contextDocs.length === 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
            <FontAwesomeIcon icon="exclamation-circle" className="text-3xl text-amber-500 mb-3" />
            <p className="text-amber-800 font-medium mb-2">No Context Documents Available</p>
            <p className="text-amber-700 text-sm">
              Please add company context documents in the Admin Panel before analyzing RFPs.
            </p>
          </div>
        ) : (
          <>
            {/* Selection Controls */}
            <div className="flex items-center justify-between py-2">
              <div className="text-sm text-slate-600">
                <strong>{selectedDocIds.length}</strong> of <strong>{contextDocs.length}</strong> selected
              </div>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={selectAll}
                  className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                >
                  Select All
                </button>
                <span className="text-slate-400">|</span>
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-sm text-slate-600 hover:text-slate-800 font-medium"
                >
                  Clear All
                </button>
              </div>
            </div>

            {/* Document List */}
            <div className="max-h-96 overflow-y-auto space-y-2 border rounded-lg p-4 bg-slate-50">
              {contextDocs.map((doc) => (
                <div
                  key={doc.document_id}
                  className={`
                    border-2 rounded-lg p-3 cursor-pointer transition-all
                    ${selectedDocIds.includes(doc.document_id)
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                    }
                  `}
                  onClick={() => toggleDoc(doc.document_id)}
                >
                  <div className="flex items-center space-x-3">
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={selectedDocIds.includes(doc.document_id)}
                      onChange={() => toggleDoc(doc.document_id)}
                      className="w-5 h-5 text-primary-600 border-slate-300 rounded focus:ring-primary-500"
                      onClick={(e) => e.stopPropagation()}
                    />

                    {/* Icon */}
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                        <FontAwesomeIcon
                          icon={getDocumentTypeIcon(doc.document_type)}
                          className="text-slate-600"
                        />
                      </div>
                    </div>

                    {/* Document Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-slate-900 truncate">
                        {doc.document_name}
                      </h4>
                      <div className="flex items-center space-x-3 text-sm text-slate-600 mt-1">
                        <span className="capitalize">{doc.document_type?.replace('_', ' ')}</span>
                        {doc.word_count && (
                          <>
                            <span>•</span>
                            <span>{doc.word_count.toLocaleString()} words</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Selected Indicator */}
                    {selectedDocIds.includes(doc.document_id) && (
                      <FontAwesomeIcon
                        icon="check-circle"
                        className="text-primary-600 text-xl"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={selectedDocIds.length === 0 || loading}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            <FontAwesomeIcon icon="arrow-right" />
            <span>Continue to Analysis ({selectedDocIds.length})</span>
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ContextDocumentSelector;
